import { Card, makeStyles, Theme } from "@material-ui/core";
import React from "react";

import { FONT_HEAVY } from "../../styles/fonts";

interface SectionCardProps {
  title: React.ReactNode;
  children?: React.ReactNode;
  headerSlot?: React.ReactNode;
  footer?: React.ReactNode;
  disableContentPadding?: boolean;
  disableHeaderDivider?: boolean;
  className?: string;
  dataTestId?: string;
}

export default function SectionCard({
  title,
  headerSlot,
  footer,
  children,
  disableContentPadding,
  disableHeaderDivider,
  className = "",
  dataTestId,
}: SectionCardProps) {
  const classes = useStyles();
  const contentClass = disableContentPadding
    ? classes.content
    : `${classes.content} ${classes.contentPadded}`;
  const headerClass = disableHeaderDivider
    ? classes.headerNoDivider
    : classes.header;

  return (
    <Card
      classes={{ root: classes.root }}
      className={className}
      elevation={0}
      data-testid={dataTestId}
    >
      <div className={headerClass}>
        <h3 className={classes.title}>{title}</h3>
        {headerSlot && <span className={classes.headerSlot}>{headerSlot}</span>}
      </div>
      <div className={contentClass}>{children}</div>
      {footer && <div className={classes.footer}>{footer}</div>}
    </Card>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `solid 1px ${theme.palette.divider}`,
    boxShadow: "none",
  },
  header: {
    borderBottom: `solid 1px ${theme.palette.divider}`,
    padding: "16px 24px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  headerNoDivider: {
    padding: "16px 24px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  title: {
    fontSize: 18,
    lineHeight: "19px",
    margin: 0,
    ...FONT_HEAVY,
  },
  headerSlot: {
    display: "inline-flex",
    alignItems: "center",
  },
  content: {
    flexGrow: 1,
    fontFamily: "Avenir, Arial, Verdana, sans-serif",
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "24px",
    letterSpacing: "0.15px",
    color: theme.palette.text.primary,
    "& .ql-editor": {
      padding: 0,
      fontFamily: "inherit",
      fontWeight: "inherit",
      fontSize: "inherit",
      lineHeight: "inherit",
      letterSpacing: "inherit",
      color: "inherit",
    },
  },
  contentPadded: {
    padding: 24,
  },
  footer: {
    padding: 16,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "flex-start",
    gap: 8,
    alignSelf: "stretch",
  },
}));
